
export type GroupByToMultimapParams<T, K, V> = {
  readonly data: ReadonlyArray<T>;
  readonly keyTransform: (value: T) => K;
  readonly valTransform: (value: T) => V;
  readonly keyBy: (key: K) => string;
}

/**
 * Creates an {@link IMultimap<K, V>} using the values in {@link GroupByToMultimapParams} to convert the data
 * into a map where the key is transformed into {@link K} by the keyTransform function and the value
 * is a {@link ReadonlyArray} of {@link V} transformed by the valTransform function.
 *
 * @param {ReadonlyArray<T>} data - the array of data to be converted into a multimap
 * @param {(value: T) => K} keyTransform - the function that will convert an item in data into a key of type {@link K}
 * @param {(value: T) => V} valTransform - the function that will convert an item in data into a value of type {@link V}
 * @param {(key: K) => string} keyBy - the function that will convert a key into a string due to the constraints of JavaScript's {@link Map} implementation,
 * for more details see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Equality_comparisons_and_sameness#a_model_for_understanding_equality_comparisons
 *
 * @returns an {@link IMultimap<K, V>}
 */
export function groupByToMultimap<T, K, V>({ data, keyTransform, valTransform, keyBy }: GroupByToMultimapParams<T, K, V>): IMultimap<K, V> {
  const map = data.reduce(
    (acc, val, _, __, key = keyTransform(val)) => (getOrPut(acc, keyBy(key), () => ([])).push(valTransform(val)), acc),
    new Map<string, V[]>()
  );

  return new Multimap<K, V>(map, keyBy);
}

/**
 * To create an instance of @type {IMultimap} use @function groupByToMultimap
 */
export interface IMultimap<K, V> {
  get(key: K): ReadonlyArray<V> | undefined
}

class Multimap<K, V> {
  private readonly keyBy: (key: K) => string
  private readonly map: ReadonlyMap<string, V[]>

  constructor(map: Map<string, V[]>, keyBy: (key: K) => string) {
    this.map = map
    this.keyBy = keyBy
  }

  get(key: K): V[] | undefined {
    const transformed = this.keyBy(key);
    return this.map.get(transformed)
  }
}

function getOrPut<K, V>(map: Map<K, V>, key: K, defaultValue: () => V): V {
  const value = map.get(key);

  if (value === undefined) {
    const def = defaultValue();
    map.set(key, def);
    return def;
  } else {
    return value;
  }
}
